import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from '@/components/Portal';
import { useDismissable } from '@/lib/useDismissable';
import { toast } from '@/lib/toast';
import { emitAppEvent } from '@/lib/appEvents';
import { useDataStore } from '@/stores/dataStore';
import { PERMISSIONS, useMyGuildPermissions } from '@/lib/guildPermissions';
import { isGuildMuted, muteGuild, unmuteGuild } from '@/lib/muteSettings';
import {
  BellIcon,
  BellOffIcon,
  ChevronDownIcon,
  CopyIcon,
  LinkIcon,
  SettingsIcon,
} from '@/components/icons';

/**
 * Discord-style right-click menu for a server icon. Cursor-anchored (Portal +
 * useLayoutEffect clamp, same pattern as the message context menu) since a
 * context menu opens at the pointer, not at a fixed trigger.
 *
 * Items: invite, mute-server (with duration submenu), settings (owner /
 * MANAGE_GUILD — ADMINISTRATOR short-circuits), copy server id. Mute is
 * client-side via lib/muteSettings (timed or until manually re-enabled).
 */
const MUTE_DURATIONS: { labelKey: string; ms: number | null }[] = [
  { labelKey: 'gmod.mute15m', ms: 15 * 60 * 1000 },
  { labelKey: 'gmod.mute1h', ms: 60 * 60 * 1000 },
  { labelKey: 'gmod.mute3h', ms: 3 * 60 * 60 * 1000 },
  { labelKey: 'gmod.mute8h', ms: 8 * 60 * 60 * 1000 },
  { labelKey: 'gmod.mute24h', ms: 24 * 60 * 60 * 1000 },
  { labelKey: 'gmod.muteUntilOn', ms: null },
];

export function ServerContextMenu(props: {
  guildId: string;
  x: number;
  y: number;
  onClose: () => void;
  onInvite: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [muteExpanded, setMuteExpanded] = useState(false);
  const selectGuild = useDataStore((s) => s.selectGuild);
  const muted = isGuildMuted(props.guildId);
  // Owner + ADMINISTRATOR short-circuit inside `can`; plain MANAGE_GUILD
  // holders get the entry too (the settings modal gates its tabs anyway).
  const { can } = useMyGuildPermissions(props.guildId);
  const canOpenSettings = can(PERMISSIONS.MANAGE_GUILD);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const m = 8;
    let nx = props.x;
    let ny = props.y;
    if (nx + r.width + m > window.innerWidth) nx = props.x - r.width;
    if (ny + r.height + m > window.innerHeight) ny = window.innerHeight - r.height - m;
    nx = Math.min(Math.max(m, nx), window.innerWidth - r.width - m);
    ny = Math.max(m, ny);
    setPos({ x: nx, y: ny });
  }, [props.x, props.y, muteExpanded]);

  useDismissable(true, ref, props.onClose);

  function openSettings(tab?: string) {
    selectGuild(props.guildId);
    emitAppEvent('guild:settings', tab ? { tab } : {});
    props.onClose();
  }

  function copyId() {
    navigator.clipboard
      ?.writeText(props.guildId)
      .then(() => toast.success(t('gmod.serverIdCopied')))
      .catch(() => undefined);
    props.onClose();
  }

  return (
    <Portal>
      <div
        ref={ref}
        style={
          pos
            ? { left: pos.x, top: pos.y }
            : { left: -9999, top: -9999, visibility: 'hidden' }
        }
        className="bg-surface fixed z-[100] min-w-[232px] overflow-hidden rounded-2xl p-1.5 shadow-overlay"
        role="menu"
      >
        {/* A closed server (no «Приглашать» for me) offers no invite. */}
        {can(PERMISSIONS.CREATE_INVITES) && (
          <MenuItem
            icon={<LinkIcon className="h-4 w-4" />}
            onClick={() => {
              props.onInvite();
              props.onClose();
            }}
          >
            {t('gmod.inviteToServerItem')}
          </MenuItem>
        )}

        {muted ? (
          <MenuItem
            icon={<BellIcon className="h-4 w-4" />}
            onClick={() => {
              unmuteGuild(props.guildId);
              toast.success(t('gmod.notificationsOn'));
              props.onClose();
            }}
          >
            {t('gmod.enableNotifications')}
          </MenuItem>
        ) : (
          <>
            <button
              type="button"
              role="menuitem"
              aria-expanded={muteExpanded}
              onClick={() => setMuteExpanded((v) => !v)}
              className="group/ctx flex min-h-10 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-white/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <BellOffIcon className="h-4 w-4 shrink-0 text-ink-muted transition-colors group-hover/ctx:text-accent" />
              <span className="flex-1 truncate">{t('gmod.muteServer')}</span>
              <ChevronDownIcon
                className={
                  'h-3.5 w-3.5 shrink-0 text-ink-muted transition-transform ' +
                  (muteExpanded ? 'rotate-0' : '-rotate-90')
                }
              />
            </button>
            {muteExpanded && (
              <div className="my-1 ml-5 border-l border-separator/70 pl-1">
                {MUTE_DURATIONS.map((d) => (
                  <MenuItem
                    key={d.labelKey}
                    onClick={() => {
                      muteGuild(props.guildId, d.ms);
                      toast.success(t('gmod.serverMuted'));
                      props.onClose();
                    }}
                  >
                    {t(d.labelKey)}
                  </MenuItem>
                ))}
              </div>
            )}
          </>
        )}

        {canOpenSettings && (
          <MenuItem icon={<SettingsIcon className="h-4 w-4" />} onClick={() => openSettings()}>
            {t('gmod.serverSettings')}
          </MenuItem>
        )}

        <div className="h-2" role="separator" />
        <MenuItem icon={<CopyIcon className="h-4 w-4" />} onClick={copyId}>
          {t('gmod.copyServerId')}
        </MenuItem>
      </div>
    </Portal>
  );
}

function MenuItem(props: {
  icon?: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={props.onClick}
      className="group/ctx flex min-h-10 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-white/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {props.icon && (
        <span className="shrink-0 text-ink-muted transition-colors group-hover/ctx:text-accent">
          {props.icon}
        </span>
      )}
      <span className="flex-1 truncate">{props.children}</span>
    </button>
  );
}
